import React, { useState } from 'react';
import { AlertView, GradientTextAction, InfoIcon, Poppins, Spacer } from 'src/common';
import Form from './Form';
import { CsvImportPreview, CsvImportGroups, CsvWorkspaceGroups } from './types';
import { Variable } from 'src/api/types';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useStateSelector } from 'src/redux';
import styled from 'styled-components';
import { exportVariablesToCsv } from './util';
import { Scope } from 'src/components/formComposed/WorkspaceVariablesForm/types';
import { CSVGroupSelector, CSVWorkspaceGroupSelector } from './CSVGroupSelector';
import { pluralize } from 'src/utils/misc';
import { SCVSummary } from '../CsvUpload';

const pluralizeVariables = (count: number) => pluralize({ count, singular: 'variable', plural: 'variables' });

const Div = styled.div`
  .head {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    margin-right: auto;
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 6px;
    display: inline-flex;
    cursor: pointer;
  }

  .summary {
    display: grid;
    grid-template-columns: 20px 1fr;
    grid-gap: 6px;

    svg {
      transform: scale(0.8);
    }

    & > div {
      display: flex;
      flex-direction: column;
    }
  }
`;

interface Props {
  originalVariables: Variable[];
  workspaceVariableNames: string[];
  scope: Scope;
  pendingImport: CsvImportPreview | null;
  onPendingImportChange: (preview: CsvImportPreview | null) => void;
  onAddNewVariable: () => void;
}

const VariableCSVReader: React.FC<Props> = ({
  originalVariables,
  workspaceVariableNames,
  scope,
  pendingImport,
  onPendingImportChange,
  onAddNewVariable,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const colors = useStateSelector(({ theme }) => theme.colors);

  const handleParsed = (preview: CsvImportPreview) => {
    onPendingImportChange(preview);
  };

  const handleGroupsChange = (groups: CsvImportGroups) => {
    if (pendingImport) {
      onPendingImportChange({
        ...pendingImport,
        groups,
      });
    }
  };

  const handleWorkspaceGroupsChange = (workspaceGroups: CsvWorkspaceGroups) => {
    if (pendingImport) {
      onPendingImportChange({
        ...pendingImport,
        workspaceGroups,
      });
    }
  };

  const handleToggle = () => {
    if (isOpen) {
      if (pendingImport) {
        onPendingImportChange(null);
      }
    }
    setIsOpen(!isOpen);
    mpEvent(MPEvents.ButtonClick, {
      button: 'Import from CSV',
      tags: ['VARIABLES'],
    });
  };

  const workspaceExistingVariables =
    pendingImport?.workspaceGroups?.existingVars.action === 'include'
      ? pendingImport?.workspaceGroups?.existingVars.variables.length
      : 0;

  const workspaceNewVariables = pendingImport?.workspaceGroups?.newVars.variables.length || 0;

  const assessmentCustomVars =
    pendingImport?.groups?.customAssessmentVars.action === 'include'
      ? pendingImport.groups.customAssessmentVars.variables.length
      : 0;

  const assessmentWorkspaceVars =
    pendingImport?.groups?.workspaceVars.action === 'include' ? pendingImport.groups.workspaceVars.variables.length : 0;

  const assessmentOverriddenVars =
    pendingImport?.groups?.overriddenVars.action === 'include'
      ? pendingImport.groups.overriddenVars.variables.length
      : 0;

  const assessmentNewVars =
    pendingImport?.groups?.newVars.action === 'include' ? pendingImport.groups.newVars.variables.length : 0;

  const pluralizeVariables = (count: number) => pluralize({ count, singular: 'variable', plural: 'variables' });

  return (
    <Div>
      <div className="head">
        <GradientTextAction
          $underline
          weight={500}
          px={14}
          onClick={() => onAddNewVariable()}
          data-cy="add-variable-btn"
        >
          Add new variable
        </GradientTextAction>
        <div>
          <GradientTextAction weight={500} $underline px={14} className="controls" onClick={handleToggle}>
            Import from CSV
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="6"
              viewBox="0 0 11 6"
              fill="none"
              style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)' }}
            >
              <path
                d="M9.88629 5.93359H0.659141C0.480523 5.93359 0.326108 5.86838 0.195643 5.73806C0.0651783 5.60759 0 5.45318 0 5.27467C0 5.09616 0.0651783 4.94164 0.195643 4.81124L4.80923 0.197722C4.93984 0.0672574 5.09426 0.00193596 5.27273 0.00193596C5.45121 0.00193596 5.60577 0.0672574 5.73612 0.197722L10.3497 4.81128C10.48 4.94164 10.5455 5.09616 10.5455 5.2747C10.5455 5.45318 10.4801 5.60759 10.3497 5.73809C10.2194 5.86852 10.0648 5.93359 9.88629 5.93359Z"
                fill={colors.gp1}
              ></path>
            </svg>
          </GradientTextAction>
        </div>
        <GradientTextAction
          $underline
          weight={500}
          px={14}
          onClick={() => {
            exportVariablesToCsv(originalVariables, scope);
            mpEvent(MPEvents.ButtonClick, {
              button: 'Export CSV',
              tags: ['VARIABLES'],
            });
          }}
        >
          Export CSV
        </GradientTextAction>
      </div>

      {isOpen && (
        <>
          <Spacer $px={16} />
          <Form
            onParsed={handleParsed}
            onClear={() => onPendingImportChange(null)}
            pendingImport={pendingImport}
            originalVariables={originalVariables}
            workspaceVariableNames={workspaceVariableNames}
            scopeType={scope.type}
          />
          {pendingImport && (
            <>
              {pendingImport.variables.length === 0 ? (
                <>
                  <Spacer $px={16} />
                  <div className="summary">
                    <InfoIcon />
                    <Poppins px={14}>No valid variables to update from the CSV file</Poppins>
                  </div>
                </>
              ) : (
                <>
                  {/* Assessment scope groups */}
                  {pendingImport.groups && (
                    <>
                      <Spacer $px={16} />
                      <AlertView>
                        <CSVGroupSelector groups={pendingImport.groups} onGroupsChange={handleGroupsChange} />
                      </AlertView>
                    </>
                  )}
                  {/* Workspace scope groups */}
                  {pendingImport.workspaceGroups && (
                    <>
                      <Spacer $px={16} />
                      <AlertView>
                        <CSVWorkspaceGroupSelector
                          groups={pendingImport.workspaceGroups}
                          onGroupsChange={handleWorkspaceGroupsChange}
                        />
                      </AlertView>
                    </>
                  )}
                  <Spacer $px={16} />
                  {pendingImport.groups ? (
                    <SCVSummary
                      items={[
                        `File validated`,
                        `${assessmentCustomVars} existing assessment ${pluralizeVariables(assessmentCustomVars)}`,
                        `${assessmentWorkspaceVars} workspace ${pluralizeVariables(assessmentWorkspaceVars)} to override`,
                        `${assessmentOverriddenVars} already overridden ${pluralizeVariables(assessmentOverriddenVars)}`,
                        `${assessmentNewVars} new ${pluralizeVariables(assessmentNewVars)} to be added`,
                      ]}
                    />
                  ) : pendingImport.workspaceGroups ? (
                    <SCVSummary
                      items={[
                        `File validated`,
                        `${workspaceExistingVariables} existing ${pluralizeVariables(workspaceExistingVariables)} ready`,
                        `${workspaceNewVariables} new ${pluralizeVariables(workspaceNewVariables)} to be added`,
                      ]}
                    />
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      )}
    </Div>
  );
};

export default VariableCSVReader;
